/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../../common/controller/signal-resource-list-binding.service';
import {DestroyRef, inject, Component, Inject, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin} from 'rxjs';
import * as _ from 'lodash';
import template from './layout-edit-page.component.html';
import {AdnSelectSearchElement} from '../../common/directives/adn-select/adn-select.component';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {ASPECT_RATIOS, ASPECT_RATIOS_TRANSLATIONS} from './layout-commons';
import {BASIC_LAYOUT_BUNCH} from '../layout-example/examples/basic-layouts';
import {LAYOUT_BUNCH} from '../layout-example/examples/other-layouts';
import {MEDIA_TYPE, MediaTypeHelper} from '../../../components/util/media-type-helper';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {TeamApiService} from '../team/team-api.service';
import {CreativeApiService} from '../../creative/creative-api.service';
import {LayoutIncludeApiService} from '../layout-include/layout-include-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {BrowserLocationService} from '../../angular-platform-providers';
import {LayoutApiService, LayoutModel} from './layout-api.service';
import {ADVERTISING_SHARED} from '../../advertising/advertising-shared';

function normalizeTeamIds(teams: any): string[] {
  return _.compact(_.map(teams || [], (team: any) => _.isString(team) ? team : team.id));
}

function ownerTeamId(model: any): string | undefined {
  const ownerTeam = _.get(model, 'ownerTeam');
  return _.isString(ownerTeam) ? ownerTeam : _.get(ownerTeam, 'id');
}

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-layout-edit-page',
  template
})
export class LayoutEditPageComponent extends SignalAdminResourceEditPageBase<LayoutModel> {
  private readonly listLifetime = inject(DestroyRef);
  readonly aspectRatioDescriptions = ASPECT_RATIOS_TRANSLATIONS;
  readonly aspectRatios = ASPECT_RATIOS;
  readonly toolbarButtons = [
    ['h1', 'h2', 'h3', 'p'],
    ['bold', 'italics', 'strikeThrough', 'ul', 'ol', 'redo', 'undo', 'clear'],
    ['insertImage', 'insertLink', 'html']
  ];
  readonly fullPageFormats = [
    {code: 'TOP_SCROLL', name: 'Top Scroll'},
    {code: 'MID_SCROLL', name: 'Mid Scroll'},
    {code: 'DOUBLE_FULLSCREEN', name: 'Double Fullscreen'}
  ];
  layouts: any = {};
  layoutIncludes: any[] = [];
  ownership = 'NETWORK';
  allPublisherTeams: any[] = [];
  allTeams: any[] = [];
  teamSearchResults: any[] = [];
  showOwnership = false;
  thumbnailSecondColumn = false;
  showDataOption = false;
  showAdnuntiusApproved = false;
  showFullPage = false;
  isSuiPlatform = false;
  allowsPoliticalAds = false;
  canUpdateLayout = false;
  needsVerificationCheck = false;
  transcodeOutputs: string[] = [];
  otherMediaTypes: any[] = [];
  restrictOn = true;
  showCreativeDetail = false;
  editThis = false;
  layoutPref = false;

  items: any[] = [];
  get displayItems(): any[] { return this.listState?.items() || []; }
  get hasItems(): boolean { return this.displayItems.length > 0; }
  get displayTotalCount(): number { return this.listState?.totalCount() || 0; }
  listState: import('../../common/controller/signal-admin-resource-list-state').SignalAdminResourceListState<any>;
  totalCount = 0;
  makeQuery: (() => void) | undefined;
  setMoreParams: ((params: any) => void) | undefined;

  private prevLayoutType = 'STANDARD';
  private listInitialized = false;
  private asyncFormSectionsReady = false;
  private moreParams: any;
  private defaultLayouts: any;

  constructor(
    @Inject(LocalNetworkProfile) private readonly layoutNetworkProfile: any,
    private readonly ngZone: NgZone,
    private readonly layoutApi: LayoutApiService,
    private readonly layoutIncludeApi: LayoutIncludeApiService,
    @Inject(CreativeApiService) private Creative: CreativeApiService,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    @Inject(LocalUserProfile) private localUserProfile: any,
    private listBinding: SignalResourceListBindingService,
    private translate: TranslateService,
    @Inject(BrowserLocationService) private location: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(layoutApi, 'layout');

    this.showDataOption = this.localNetworkInfo.hasProductFeedProducts();
    this.isSuiPlatform = this.layoutNetworkProfile.isSuiPlatform();
    this.allowsPoliticalAds = this.layoutNetworkProfile.isPoliticalAdsPlatform();
    // note we can't check AX_PUBLISHER_TEAM_ADMIN because we don't know the ownerTeam as yet so it is checked
    // in updateCanUpdateLayout
    this.canUpdateLayout = LocalUserPermissions.hasNetwork('MANAGE_LAYOUTS') || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN');
    this.needsVerificationCheck = this.layoutNetworkProfile.isVerificationCheckNetwork(LocalUserPermissions.hasNetwork('MANAGE_SYSTEM'));
    this.transcodeOutputs = this.layoutNetworkProfile.getTranscodeOutputs();
    this.otherMediaTypes = MediaTypeHelper.getOtherMediaTypes();

    const isSpecialUser = localUserProfile.get('username') === 'adnuntius@adnuntius.com' || localUserProfile.get('username') === 'broker1@adnuntius.com';
    this.showAdnuntiusApproved = this.layoutNetworkProfile.isExperimental() && isSpecialUser;
    const isLocalhost = location.hostname === 'localhost';
    this.showFullPage = isLocalhost || (this.layoutNetworkProfile.isPureMarketplacePlatform() && isSpecialUser);

    forkJoin([
      this.teamApi.listOptions({minimal: true, filterBy: 'type', filterByValue: 'AX_PUBLISHER'}),
      this.teamApi.listOptions({minimal: true, filterBy: 'type', filterByValue: 'AX_ADVERTISER'})
    ]).subscribe(([publisherPage, advertiserPage]) => {
      this.ngZone.run(() => {
        this.allPublisherTeams = publisherPage.results;
        this.showOwnership = LocalUserPermissions.hasNetwork('MANAGE_LAYOUTS') && (isLocalhost || this.allPublisherTeams.length > 0);
        this.thumbnailSecondColumn = this.ownership === 'SPECIFIC_TEAM' || this.showOwnership;
        this.allTeams = advertiserPage.results;
        this.asyncFormSectionsReady = true;
        this.resolveOwnerTeamReference();
        this.refreshTeamSearchResults();
      });
    });

    this.defaultLayouts = {
      STANDARD: BASIC_LAYOUT_BUNCH.standard,
      HTML: BASIC_LAYOUT_BUNCH.html,
      THIRD_PARTY: BASIC_LAYOUT_BUNCH.thirdParty,
      VAST: BASIC_LAYOUT_BUNCH.vastUpload,
      VAST_TEXT: BASIC_LAYOUT_BUNCH.vastExternalHosted,
      NATIVE: LAYOUT_BUNCH.nativeBasic
    };
  }

  private initialiseCreativeList(): void {
    if (this.editingModel?.id && !this.listInitialized) {
      this.listInitialized = true;
      this.moreParams = {layout: this.editingModel.id, excludeInvalid: this.restrictOn};
      this.listBinding.create(this.Creative, 'fullCreativesforLayout', this.listLifetime, {
      baseTable: 'creative',
        moreParams: this.moreParams,
        noInitQuery: true
      }, this);
      if (!this.isNew) {
        this.makeQuery?.();
      }
    }
  }

  protected override afterEditingModelReset(): void {
    this.editingModel.layoutType = this.editingModel.layoutType || 'STANDARD';
    this.editingModel.layoutComponents = this.editingModel.layoutComponents || [];
    if (this.editingModel.layoutType === 'VAST_2') {
      this.editingModel.layoutType = 'VAST';
    }
    this.editingModel.political = this.editingModel.political || false;
    this.editingModel.fullPage = this.editingModel.fullPage || false;
    if (this.editingModel.fullPage) {
      this.editingModel.fullPageFormat = this.editingModel.fullPageFormat || 'TOP_SCROLL';
    }

    this.restrictOn = true;
    this.layouts = _.cloneDeep(this.defaultLayouts);
    if (!this.isNew) {
      this.layouts[this.editingModel.layoutType] = _.pick(this.editingModel, ['renderTemplate', 'layoutComponents']);
    }
    this.prevLayoutType = this.editingModel.layoutType;

    _.forEach(this.editingModel.layoutComponents, (c: any) => {
      if (c.type === 'CHOICE' && _.isArray(c.options)) {
        c.optionsAsString = JSON.stringify(c.options);
      }
    });
    _.forEach(this.editingModel.layoutComponents, (ac: any) => {
      this.makeDefaultMandatory(ac);
      this.initializeAssetComponent(ac);
    });
    this.getLayoutIncludes();
    this.editingModel.renderOption = this.editingModel.renderOption || null;
    this.editingModel.teams = normalizeTeamIds(this.editingModel.teams);
    this.resolveOwnerTeamReference();
    this.updateCanUpdateLayout();
    this.refreshTeamSearchResults();
    this.initialiseCreativeList();

    this.ownership = (this.editingModel.ownerTeam || (this.editingModel.teams && this.editingModel.teams.length > 0) || this.LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN'))
      ? 'SPECIFIC_TEAM' : 'NETWORK';
    this.thumbnailSecondColumn = this.ownership === 'SPECIFIC_TEAM' || this.showOwnership;
    this.editingModel.approvalStatus = this.editingModel.approvalStatus || 'DEFAULT';

    _.forEach(this.editingModel.layoutComponents, (lc: any) => {
      if (lc.mimeTypes) {
        lc.aspectRatio = lc.customAspectRatioWidth > 0 && lc.customAspectRatioHeight > 0 ? 'CUSTOM' : lc.aspectRatio;
      }
      this.initializeAssetComponent(lc);
    });

    if (!this.listState?.items().length && !this.listState?.loading() && !this.isNew) {
      this.makeQuery?.();
    }
    this.getLayoutIncludes();
    this.editingModel.renderOption = this.editingModel.renderOption || null;
    _.set(this.editingModel, 'teams', normalizeTeamIds(this.editingModel.teams));
    this.refreshTeamSearchResults();
    this.updateCanUpdateLayout();
    if (this.asyncFormSectionsReady) {
      this.resolveOwnerTeamReference();
      this.updateCanUpdateLayout();
      this.refreshTeamSearchResults();
    }
  }

  protected override prepareForSubmit(model: LayoutModel): void {
    if (this.ownership === 'NETWORK') {
      model.teams = [];
    }

    model.renderTemplate = this.layouts[model.layoutType].renderTemplate;
    model.layoutComponents = this.layouts[model.layoutType].layoutComponents;

    _.forEach(model.layoutComponents, (lc: any) => {
      if (lc.aspectRatio === 'CUSTOM') {
        if (lc.customAspectRatioWidth > 0 && lc.customAspectRatioHeight > 0) {
          lc.aspectRatio = null;
        } else {
          lc.aspectRatio = null;
          lc.customAspectRatioHeight = null;
          lc.customAspectRatioWidth = null;
        }
      } else {
        lc.aspectRatio = lc.aspectRatio ? lc.aspectRatio : null;
        lc.customAspectRatioHeight = null;
        lc.customAspectRatioWidth = null;
      }
    });

    if (!model.fullPage) {
      model.fullPageFormat = null;
    }

    _.forEach(model.layoutComponents, (ac: any) => {
      if (ac.type === 'ASSET' && ac.maxFileSizeKb) {
        ac.maxFileSizeBytes = ac.maxFileSizeKb * 1024;
      }
      if (ac.type === 'TEXT' && ac.textType === 'CODE') {
        ac.maxLength = 0;
      }
      if (ac.type === 'CHOICE') {
        try {
          ac.options = JSON.parse(ac.optionsAsString);
        } catch (e) {
          // server will validate
        }
      }
    });
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.allTeams, 'id');
    this.onTeamsChange();
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.onTeamsChange();
    this.markExternalFormDirty();
  }

  onOwnershipChange(): void {
    this.thumbnailSecondColumn = this.ownership === 'SPECIFIC_TEAM' || this.showOwnership;
    this.updateCanUpdateLayout();
  }

  teamSearch = (searchElement: AdnSelectSearchElement): void => {
    this.teamApi.listOptions({
      minimal: true,
      filterByLike: searchElement.search,
      filterBy: 'name',
      teamTypes: 'AX_ADVERTISER',
      pageSize: 50
    }).subscribe((page) => {
      this.ngZone.run(() => {
        this.refreshTeamSearchResults(page.results);
      });
    });
  };

  onTeamsChange(): void {
    this.refreshTeamSearchResults(this.teamSearchResults);
  }

  restrictCondition(): void {
    this.moreParams.excludeInvalid = this.restrictOn;
    this.setMoreParams?.(this.moreParams);
    this.makeQuery?.();
  }

  typeSwitch(): void {
    if (this.isNew) {
      return;
    }
    const prevLayoutDetails = this.layouts[this.prevLayoutType];
    this.layouts[this.editingModel.layoutType] = _.cloneDeep(prevLayoutDetails);
    this.prevLayoutType = this.editingModel.layoutType;
  }

  import(): void {
    this.layouts[this.editingModel.layoutType] = this.defaultLayouts[this.editingModel.layoutType];
    this.markExternalFormDirty();
  }

  addAc(): void {
    this.ensureLayoutComponents();
    const data = this.generateUniqueTagAndName('Image ', 'layoutComponents');
    this.layouts[this.editingModel.layoutType].layoutComponents.push({
      tag: data.tag,
      name: data.name,
      mediaType: 'image',
      maxFileSizeBytes: 204800,
      maxFileSizeKb: 204800 / 1024,
      mimeTypes: MediaTypeHelper.getImageMimeTypes(),
      assets: [],
      type: 'ASSET',
      saveEarlyForPreview: true,
      required: 'MANDATORY'
    });
    this.markExternalFormDirty();
  }

  addUrl(): void {
    this.ensureLayoutComponents();
    const data = this.generateUniqueTagAndName('URL ', 'layoutComponents');
    this.layouts[this.editingModel.layoutType].layoutComponents.push({
      type: 'URL',
      tag: data.tag,
      name: data.name,
      required: 'MANDATORY'
    });
    this.markExternalFormDirty();
  }

  addTextAc(): void {
    this.ensureLayoutComponents();
    const data = this.generateUniqueTagAndName('Text ', 'layoutComponents');
    this.layouts[this.editingModel.layoutType].layoutComponents.push({
      tag: data.tag,
      name: data.name,
      minLength: 0,
      maxLength: 50,
      type: 'TEXT',
      required: 'MANDATORY',
      textType: 'TEXT'
    });
    this.markExternalFormDirty();
  }

  addChoiceAc(): void {
    this.ensureLayoutComponents();
    const data = this.generateUniqueTagAndName('Choice ', 'layoutComponents');
    const defaultOptions = ['default1', 'default2'];
    this.layouts[this.editingModel.layoutType].layoutComponents.push({
      tag: data.tag,
      name: data.name,
      type: 'CHOICE',
      display: 'COMBO',
      options: defaultOptions,
      optionsAsString: JSON.stringify(defaultOptions),
      required: 'MANDATORY'
    });
    this.markExternalFormDirty();
  }

  removeComponent(ac: any): void {
    this.layouts[this.editingModel.layoutType].layoutComponents = _.filter(
      this.layouts[this.editingModel.layoutType].layoutComponents,
      (sAc: any) => sAc.tag !== ac.tag
    );
    this.markExternalFormDirty();
  }

  toggleEditAc(ac: any): void {
    ac.edited = !ac.edited;
    this.assignMediaType(ac);
    this.markExternalFormDirty();
  }

  sortUp(ac: any, sortDown = false): void {
    let lcs = _.cloneDeep(this.layouts[this.editingModel.layoutType].layoutComponents);
    let count = 1;
    _.forEach(lcs, (lc: any) => {
      let changer = (lc.tag === ac.tag && lc.type === ac.type) ? -15 : 0;
      changer = changer * (sortDown ? -1 : 1);
      lc.order = count + changer;
      count += 10;
    });
    lcs = _.sortBy(lcs, 'order');
    this.layouts[this.editingModel.layoutType].layoutComponents = _.map(lcs, (lc: any) => {
      delete lc.order;
      return lc;
    });
    this.markExternalFormDirty();
  }

  sortDown(ac: any): void {
    this.sortUp(ac, true);
  }

  mediaTypesLabel(mimeTypes: string | string[]): string {
    const types = _.isArray(mimeTypes) ? mimeTypes : [mimeTypes];
    const mediaTypes = _.map(types, (mimeType) => MediaTypeHelper.getMediaType(mimeType));
    const translations = _.map(_.uniqBy(_.compact(mediaTypes), 'id'), (mediaType: any) => this.translate.instant('media.' + mediaType.id));
    return translations.join(', ');
  }

  updateMimeTypes(ac: any): void {
    if (ac.mediaType === 'image') {
      ac.mimeTypes = MediaTypeHelper.getImageMimeTypes();
    } else if (ac.mediaType === 'video') {
      ac.mimeTypes = MediaTypeHelper.getVideoMimeTypes();
    } else if (ac.mediaType === 'audio') {
      ac.mimeTypes = MediaTypeHelper.getAudioMimeTypes();
    } else if (ac.mediaType === 'other') {
      ac.mimeTypes = [];
    } else {
      ac.mimeTypes = MEDIA_TYPE[ac.mediaType]?.mimeTypes || [];
    }

    const mediaTypes = ['image', 'html', 'video', 'audio', 'other'];
    _.forEach(mediaTypes, (mediaType: string) => {
      ac.tag = ac.tag.replace(mediaType, ac.mediaType);
    });
  }

  updateOtherMediaType(ac: any): void {
    ac.mimeTypes = ac.mediaTypeSel ? MEDIA_TYPE[ac.mediaTypeSel.id].mimeTypes : [];
  }

  private getLayoutIncludes(): void {
    const layoutIncludeIds = _.map(this.editingModel.layoutIncludes, 'id');
    if (layoutIncludeIds.length < 1) {
      this.layoutIncludes = [];
      return;
    }
    this.layoutIncludeApi.getMany(layoutIncludeIds).subscribe({
      next: (data) => {
        this.ngZone.run(() => {
          this.layoutIncludes = _.map(data.results, (li: any) => {
            if ((li.renderTemplate || '').length > 1000) {
              li.renderTemplateShort = li.renderTemplate.substring(0, 1200);
            }
            return li;
          });
        });
      }
    });
  }

  private makeDefaultMandatory(ac: any): void {
    if (!ac.required) {
      ac.required = 'MANDATORY';
    }
  }

  private initializeAssetComponent(ac: any): void {
    if (ac.type !== 'ASSET') {
      return;
    }
    ac.maxFileSizeKb = ac.maxFileSizeBytes > 0 ? ac.maxFileSizeBytes / 1024 : 0;
    this.assignMediaType(ac);
  }

  private assignMediaType(ac: any): void {
    if (!ac.mediaType) {
      if (_.isArray(ac.mimeTypes) && ac.mimeTypes.length > 0) {
        const mediaType: any = MediaTypeHelper.getMediaType(ac.mimeTypes[0]);
        if (!mediaType) {
          ac.mediaType = 'other';
          ac.mediaTypeSel = null;
          return;
        }
        ac.mediaType = mediaType.image ? 'image' : mediaType.id;
        ac.mediaType = mediaType.video ? 'video' : ac.mediaType;
        ac.mediaType = mediaType.audio ? 'audio' : ac.mediaType;
        if (mediaType.other) {
          ac.mediaType = 'other';
          ac.mediaTypeSel = mediaType;
        }
      } else {
        ac.mediaType = 'image';
      }
    }
  }

  private ensureLayoutComponents(): void {
    this.layouts[this.editingModel.layoutType].layoutComponents = this.layouts[this.editingModel.layoutType].layoutComponents || [];
  }

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam || !this.allPublisherTeams.length) {
      return;
    }
    const ownerId = _.isString(this.editingModel.ownerTeam) ? this.editingModel.ownerTeam : this.editingModel.ownerTeam.id;
    const match = _.find(this.allPublisherTeams, ['id', ownerId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    }
  }

  updateCanUpdateLayout(): void {
    const teamId = ownerTeamId(this.editingModel);
    this.canUpdateLayout = this.LocalUserPermissions.hasNetwork('MANAGE_LAYOUTS')
      || this.LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN')
      || (!!teamId && this.LocalUserPermissions.has('AX_PUBLISHER_TEAM_ADMIN', teamId));
  }

  private refreshTeamSearchResults(extraResults: any[] = []): void {
    const selectedIds = this.editingModel?.teams || [];
    const selectedTeams = _.filter(this.allTeams, (team: any) => selectedIds.indexOf(team.id) >= 0);
    this.teamSearchResults = _.uniqBy([...selectedTeams, ...extraResults], 'id');
  }

  private generateUniqueTagAndName(base: string, constraintType: string, count = 1): {tag: string; name: string} {
    const name = base + (count > 1 ? count : '');
    return _.findIndex(this.layouts[this.editingModel.layoutType][constraintType], ['name', name]) < 0
      ? {tag: name.toLowerCase().replace(' ', ''), name}
      : this.generateUniqueTagAndName(base, constraintType, count + 1);
  }
}
